/**
 * Item Variants Summary Widget — interactive variant section for template items.
 * Filter pills, collapsible attribute groups, thumbnails, prices, navigation.
 * When has_variants === 0: shows enable-variants toggle (consistent with create mode).
 * When has_variants === 1 && !variant_of: shows variant summary table.
 * When variant_of is set: hidden (variant child items don't show this).
 * Plan 135 + quick-fix grouping/filtering enhancement.
 */

import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Package, ChevronRight, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { CARD } from "@/config/design-tokens";
import type { ShopifySectionWidgetProps } from "../types";
import { formatPrice } from "@/lib/format/currency";
import { buildDocRoute } from "@/lib/config/docRoutes";
import { useDetailPageTabs } from "@/contexts/DetailPageTabsContext";
import { ItemTemplateToggleWidget } from "./ItemTemplateToggleWidget";
import { useTemplateVariantsSummary } from "../../../../hooks/useTemplateVariantsSummary";
import type { VariantSummaryItem } from "../../../../hooks/useTemplateVariantsSummary";

// --- Filter + grouping ---

type Filters = Record<string, string>;

function useVariantFiltering(
  variants: VariantSummaryItem[],
  attributeOptions: Record<string, string[]>,
  filters: Filters,
) {
  return useMemo(() => {
    const attrNames = Object.keys(attributeOptions);
    const groupAttr = attrNames[0]; // group by first attribute

    // Apply filters
    const filtered = variants.filter((v) =>
      Object.entries(filters).every(
        ([attr, val]) => v.attributes[attr] === val,
      ),
    );

    // Group by first attribute
    const groups = new Map<string, VariantSummaryItem[]>();
    if (groupAttr) {
      for (const v of filtered) {
        const key = v.attributes[groupAttr] ?? "Other";
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(v);
      }
    } else {
      groups.set("All", filtered);
    }

    return { filtered, groups, groupAttr };
  }, [variants, attributeOptions, filters]);
}

// --- Sub-components ---

function SectionHeader({
  total,
  filteredCount,
  attributeCount,
  hasFilters,
  onClearFilters,
}: {
  total: number;
  filteredCount: number;
  attributeCount: number;
  hasFilters: boolean;
  onClearFilters: () => void;
}) {
  const subtitle =
    total === 0
      ? "No variants"
      : hasFilters
        ? `Showing ${filteredCount} of ${total} variants`
        : `${total} variant${total !== 1 ? "s" : ""} across ${attributeCount} attribute${attributeCount !== 1 ? "s" : ""}`;

  return (
    <div className="mb-5 flex items-baseline justify-between">
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Variants
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
      </div>
      {hasFilters && (
        <button
          type="button"
          onClick={onClearFilters}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <X className="h-3 w-3" />
          Clear
        </button>
      )}
    </div>
  );
}

function FilterStrip({
  options,
  filters,
  onToggle,
}: {
  options: Record<string, string[]>;
  filters: Filters;
  onToggle: (attr: string, val: string) => void;
}) {
  const entries = Object.entries(options);
  if (entries.length === 0) return null;

  return (
    <div className="mb-5 space-y-3">
      {entries.map(([attribute, values]) => (
        <div key={attribute}>
          <span className="text-xs font-medium text-muted-foreground">
            {attribute}
          </span>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {values.map((v) => {
              const isActive = filters[attribute] === v;
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => onToggle(attribute, v)}
                  className="focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-xs"
                >
                  <Badge
                    variant={isActive ? "default" : "outline"}
                    className={cn(
                      "cursor-pointer text-xs transition-colors",
                      isActive && "bg-primary text-primary-foreground",
                    )}
                  >
                    {v}
                  </Badge>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function VariantGroupHeader({
  label,
  count,
  image,
  isOpen,
  onToggle,
}: {
  label: string;
  count: number;
  image: string | null;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center gap-3 py-3 text-left transition-colors hover:bg-muted/30"
    >
      <ChevronDown
        className={cn(
          "h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform",
          !isOpen && "-rotate-90",
        )}
      />
      {image && (
        <div className="h-8 w-8 flex-shrink-0 overflow-hidden rounded-xs bg-muted">
          <img src={image} alt={label} className="h-full w-full object-cover" />
        </div>
      )}
      <span className="flex-1 text-sm font-medium">{label}</span>
      <span className="text-xs text-muted-foreground">
        {count} variant{count !== 1 ? "s" : ""}
      </span>
    </button>
  );
}

function VariantRow({
  variant,
  templateImage,
  secondaryAttrs,
  onClick,
}: {
  variant: VariantSummaryItem;
  templateImage: string | null;
  secondaryAttrs: [string, string][];
  onClick: () => void;
}) {
  const imgSrc = variant.image || templateImage || null;

  return (
    <div
      role="link"
      tabIndex={0}
      className="flex cursor-pointer items-center gap-3 py-2.5 pl-7 pr-1 transition-colors hover:bg-muted/50"
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="h-8 w-8 flex-shrink-0 overflow-hidden rounded-xs bg-muted">
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={variant.item_name}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Package className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          {secondaryAttrs.map(([attr, val]) => (
            <Badge key={attr} variant="secondary" className="text-[11px]">
              {val}
            </Badge>
          ))}
        </div>
      </div>

      <span className="flex-shrink-0 text-sm tabular-nums text-muted-foreground">
        {formatPrice(variant.price_list_rate, "THB")}
      </span>

      <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="space-y-3">
      <div className="mb-5">
        <Skeleton className="mb-2 h-3 w-16" />
        <div className="flex gap-1.5">
          {[14, 12, 16, 10].map((w, i) => (
            <Skeleton key={i} className={`h-5 rounded-full`} style={{ width: w * 4 }} />
          ))}
        </div>
      </div>
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-3 py-3">
          <Skeleton className="h-10 w-10 rounded-sm" />
          <Skeleton className="h-4 w-32" />
          <div className="flex-1" />
          <Skeleton className="h-4 w-16" />
        </div>
      ))}
    </div>
  );
}

const EmptyState = () => (
  <div className="flex flex-col items-center py-8 text-center">
    <div className="mb-3 flex h-12 w-12 items-center justify-center rounded bg-muted">
      <Package className="h-6 w-6 text-muted-foreground" />
    </div>
    <p className="text-sm font-medium">No variants yet</p>
    <p className="mt-1 text-xs text-muted-foreground">
      Add variants by configuring attributes in the Variants tab.
    </p>
  </div>
);

const ErrorState = () => (
  <p className="py-6 text-center text-sm text-muted-foreground">
    Unable to load variants. Pull to refresh or try again later.
  </p>
);

// --- Main widget ---

export function ItemVariantsSummaryWidget({
  section,
  formData,
  onFieldChange,
  disabled,
}: ShopifySectionWidgetProps) {
  const navigate = useNavigate();
  const { setActiveTab } = useDetailPageTabs();
  const [filters, setFilters] = useState<Filters>({});
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set(),
  );

  const hasVariants =
    formData.has_variants === 1 || formData.has_variants === true;
  // Frappe returns "" for unset Link fields, null from get_list, undefined if field missing
  const variantOf = typeof formData.variant_of === "string" && formData.variant_of.trim()
    ? formData.variant_of
    : null;
  const isTemplate = hasVariants && !variantOf;
  const itemCode: string = formData.item_code ?? formData.name ?? "";

  const { data, isLoading, error } = useTemplateVariantsSummary(
    itemCode,
    isTemplate,
  );

  const { filtered, groups, groupAttr } = useVariantFiltering(
    data?.variants ?? [],
    data?.attribute_options ?? {},
    filters,
  );

  // Hide for bookable items — variants not relevant for bookings
  if (formData?.is_bookable === 1 || formData?.is_bookable === true) return null;

  // If this is a variant child, show parent template link
  if (variantOf) {
    return (
      <Card className={CARD}>
        <CardContent className="p-6 sm:p-8">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Variant of
          </h3>
          <button
            type="button"
            className="flex w-full items-center gap-3 rounded-lg p-3 text-left transition-colors hover:bg-muted/50"
            onClick={() => navigate(buildDocRoute("Item", variantOf))}
          >
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-muted">
              <Package className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{variantOf}</p>
              <p className="text-xs text-muted-foreground">Template item</p>
            </div>
            <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
          </button>
        </CardContent>
      </Card>
    );
  }

  // If not a template yet, delegate to the centralized toggle widget (same as create mode)
  if (!isTemplate) {
    return (
      <ItemTemplateToggleWidget
        section={section}
        formData={formData}
        onFieldChange={onFieldChange}
        disabled={disabled}
      />
    );
  }

  // If template mode is on but we have no summary data yet (e.g. just toggled
  // ON and not saved — DB still has has_variants=0 so API returns 417), show
  // the toggle+builder instead of the summary card or error. (OPS-1528)
  if (isLoading || !data) {
    return (
      <ItemTemplateToggleWidget
        section={section}
        formData={formData}
        onFieldChange={onFieldChange}
        disabled={disabled}
      />
    );
  }

  const handleRowClick = (variantItemCode: string) => {
    navigate(buildDocRoute("Item", variantItemCode));
  };

  const handleFilterToggle = (attr: string, val: string) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (next[attr] === val) {
        delete next[attr];
      } else {
        next[attr] = val;
      }
      return next;
    });
  };

  const handleClearFilters = () => setFilters({});

  const toggleGroup = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const attributeCount = data ? Object.keys(data.attribute_options).length : 0;
  const hasFilters = Object.keys(filters).length > 0;
  const hasMultipleAttrs = attributeCount > 1;

  // Find representative image per group (first variant's image in that group)
  const groupImage = (variants: VariantSummaryItem[]) =>
    variants[0]?.image || data?.template.image || null;

  return (
    <Card className={CARD}>
      <CardContent className="p-6 sm:p-8">
        <SectionHeader
          total={data?.total_variants ?? 0}
          filteredCount={filtered.length}
          attributeCount={attributeCount}
          hasFilters={hasFilters}
          onClearFilters={handleClearFilters}
        />

        {isLoading && <SkeletonRows />}

        {!isLoading && error && <ErrorState />}

        {!isLoading && !error && data && data.total_variants === 0 && (
          <EmptyState />
        )}

        {!isLoading && !error && data && data.total_variants > 0 && (
          <>
            <FilterStrip
              options={data.attribute_options}
              filters={filters}
              onToggle={handleFilterToggle}
            />

            {filtered.length === 0 && hasFilters && (
              <p className="py-6 text-center text-sm text-muted-foreground">
                No variants match these filters.
              </p>
            )}

            {hasMultipleAttrs && groupAttr ? (
              // Grouped view — group by first attribute
              <div className="space-y-1">
                {Array.from(groups.entries()).map(
                  ([groupKey, groupVariants]) => {
                    const isOpen = !collapsedGroups.has(groupKey);
                    const secondaryAttrNames = Object.keys(
                      data.attribute_options,
                    ).filter((a) => a !== groupAttr);

                    return (
                      <div key={groupKey}>
                        <VariantGroupHeader
                          label={groupKey}
                          count={groupVariants.length}
                          image={groupImage(groupVariants)}
                          isOpen={isOpen}
                          onToggle={() => toggleGroup(groupKey)}
                        />
                        {isOpen && (
                          <div className="divide-y divide-border/30">
                            {groupVariants.map((variant) => (
                              <VariantRow
                                key={variant.item_code}
                                variant={variant}
                                templateImage={data.template.image}
                                secondaryAttrs={secondaryAttrNames.map(
                                  (a) =>
                                    [a, variant.attributes[a] ?? "—"] as [
                                      string,
                                      string,
                                    ],
                                )}
                                onClick={() =>
                                  handleRowClick(variant.item_code)
                                }
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  },
                )}
              </div>
            ) : (
              // Flat view — single attribute or no attributes
              <div className="divide-y divide-border/40">
                {filtered.map((variant) => (
                  <VariantRow
                    key={variant.item_code}
                    variant={variant}
                    templateImage={data?.template.image ?? null}
                    secondaryAttrs={Object.entries(variant.attributes)}
                    onClick={() => handleRowClick(variant.item_code)}
                  />
                ))}
              </div>
            )}

            <button
              type="button"
              className="mt-4 w-full text-center text-sm text-primary hover:underline"
              onClick={() => setActiveTab("variations", true)}
            >
              Manage variants
            </button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
